import React, { useCallback, useEffect, useState } from 'react'
import { useSocketContextData } from 'src/context/socket'
import { Button, Tooltip, Divider } from '@nextui-org/react'
import { useDropzone } from 'react-dropzone'
import api from 'src/lib/api'
import { Icon } from '@iconify/react'
import { motion } from 'framer-motion'
import CoverPageContentDetails from 'src/pages/powerdoc-view/editor/components/CoverPage/CoverPageContentDetails'
import CoverPageContentDesign from 'src/pages/powerdoc-view/editor/components/CoverPage/CoverPageContentDesign'
import { debounce } from 'lodash-es'

import Mixpanel from 'src/lib/mixpanel'
import CoverPageModel1 from './Designs/Model1'
import CoverPageModel2 from './Designs/Model2'
import CoverPageModel4 from './Designs/Model4'
import CoverPageModel3 from './Designs/Model3'

export default function CoverPage({ doc, isVisible, close, variant }: any) {
  const [title, setTitle] = useState<string>(doc?.title)
  const [documentDate, setDocumentDate] = useState<string>(doc?.documentDate)
  const [createdBy, setCreatedBy] = useState<string>(doc?.createdBy)
  const [proposedTo, setProposedTo] = useState<string>(doc?.proposedTo)
  const [logo, setLogo] = useState<string>(doc?.coverLogo)
  const [file, setFile] = useState<File>()
  const [logoSize, setLogoSize] = useState<'sm' | 'md' | 'lg'>(doc?.coverLogoSize)
  const [isLeftSidebarOpen, setLeftSidebarOpen] = useState(true)
  const [currentSidebarContent, setCurrentSidebarContent] = useState('details')
  const [coverPageDesign, setCoverPageDesign] = useState<number>(doc?.coverPageDesign || -1)
  const [documentProjectID, setDocumentProjectID] = useState<number>(doc?.documentProjectID || '')
  const handleCloseSidebar = () => {
    setLeftSidebarOpen(false)
  }

  const handleSidebarToggle = (content) => {
    setLeftSidebarOpen(true)
    setCurrentSidebarContent(content)
  }

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        close?.()
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [close])
  const { io } = useSocketContextData()
  const updateCoverPageDetails = async () => {
    await io.emitWithAck('update_document', {
      token: localStorage.getItem('accessToken'),
      documentUUID: doc?.uuid,
      variant,
      title,
      documentDate,
      createdBy,
      proposedTo,
      documentProjectID,
      coverPageDesign,
      logoSize,
    })
  }
  const debouncedUpdate = useCallback(
    debounce(() => {
      console.log({
        token: localStorage.getItem('accessToken'),
        documentUUID: doc?.uuid,
        variant,
        title,
        documentDate,
        createdBy,
        proposedTo,
        documentProjectID,
        coverPageDesign,
        logoSize,
      })
      io.emit('update_document', {
        token: localStorage.getItem('accessToken'),
        documentUUID: doc?.uuid,
        variant,
        title,
        documentDate,
        createdBy,
        proposedTo,
        documentProjectID,
        coverPageDesign,
        logoSize,
      })
    }, 300), // Adjust debounce delay as needed
    [doc?.uuid, variant, title, documentDate, createdBy, proposedTo, documentProjectID, coverPageDesign, logoSize],
  )

  useEffect(() => {
    debouncedUpdate()
    // Cleanup to cancel debounce on unmount or updates
    return () => debouncedUpdate.cancel()
  }, [
    debouncedUpdate,
    doc?.uuid,
    variant,
    title,
    documentDate,
    createdBy,
    proposedTo,
    documentProjectID,
    coverPageDesign,
    logoSize,
  ])
  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      if (acceptedFiles[0]) {
        const preview = URL.createObjectURL(acceptedFiles[0])
        setLogo(preview)
        const formData = new FormData()
        formData.append('file', acceptedFiles[0])
        const res = await api.post(`/powerdocs/${doc?.uuid}/${variant}/logo`, formData)
      }
    },
    [variant],
  )

  const deleteFile = async () => {
    setLogo('')
    const formData = new FormData()
    formData.append('logo', '')
    const res = await api.post(`/powerdocs/${doc?.uuid}/${variant}/logo`, formData)
  }
  const { getRootProps, getInputProps, open } = useDropzone({
    onDrop,
    accept: 'image/*', // Accept specific file types if needed
    maxFiles: 1, // Limit to one file at a time
  })

  const coverPageDetails = {
    documentDate,
    setDocumentDate,
    documentProjectID,
    setDocumentProjectID,
    title,
    setTitle,
    createdBy,
    setCreatedBy,
    proposedTo,
    setProposedTo,
    updateCoverPageDetails,
    coverPageDesign,
    setCoverPageDesign,
    logo,
    open,
    deleteFile,
    getRootProps,
    getInputProps,
    logoSize,
    setLogoSize,
  }
  useEffect(() => {
    if (coverPageDesign > -1) updateCoverPageDetails()
  }, [coverPageDesign])
  return (
    isVisible && (
      <div className="absolute inset-0 flex justify-center z-[60]">
        <div className="absolute inset-0 overflow-hidden rounded-b-lg bg-white flex flex-1">
          {/* Left sidebar */}
          <div className="fixed top-[60px] bottom-[60px] left-[30px] w-[100px] max-w-[100px] h-[calc(100vh-120px)] bg-white border-1 border-gray-200 flex flex-col items-center py-4 px-2 rounded-2xl z-40 gap-6">
            {/* Middle Section */}
            <div className="flex flex-col items-center gap-4 w-full">
              <p className="text-xs text-gray-500 tracking-widest text-center">DETAILS</p>
              <Tooltip content="Details" color="foreground" showArrow={true} placement="right">
                <Button
                  onPress={() => {
                    Mixpanel.track('Cover Page Details button clicked', {
                      button_name: 'Details',
                      page_name: window.location.pathname,
                      location: 'Cover Page Sidebar',
                    })
                    handleSidebarToggle('details')
                  }}
                  variant="light"
                  isIconOnly
                >
                  <Icon icon="lucide:clipboard-type" className="text-secondary" width="30" />
                </Button>
              </Tooltip>
            </div>

            {/* Bottom Section */}
            <div className="flex flex-col items-center gap-4 w-full">
              <Divider className="h-[3px]" />
              <p className="text-xs text-gray-500 tracking-widest text-center">DESIGN</p>
              <Tooltip content="Design" color="foreground" showArrow={true} placement="right">
                <Button
                  onPress={() => {
                    Mixpanel.track('Cover Page Design button clicked', {
                      button_name: 'Design',
                      page_name: window.location.pathname,
                      location: 'Cover Page Sidebar',
                    })
                    handleSidebarToggle('design')
                  }}
                  variant="light"
                  isIconOnly
                >
                  <Icon icon="lucide:layout-template" className="text-secondary" width="30" />
                </Button>
              </Tooltip>
            </div>
          </div>

          {/* Left sidebar content */}
          {isLeftSidebarOpen && (
            <motion.div
              key={currentSidebarContent}
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -10 }}
              transition={{ duration: 0.2 }}
              className="absolute top-[60px] bottom-[60px] left-[140px] w-[300px] h-[calc(100vh-120px)] bg-white border rounded-xl p-5 z-50 overflow-y-scroll"
            >
              {currentSidebarContent === 'details' && (
                <CoverPageContentDetails
                  onClose={handleCloseSidebar}
                  coverPageDetails={coverPageDetails}
                  updateCoverPageDetails={updateCoverPageDetails}
                />
              )}
              {currentSidebarContent === 'design' && (
                <CoverPageContentDesign onClose={handleCloseSidebar} coverPageDesignProps={coverPageDetails} />
              )}
            </motion.div>
          )}

          {/* Cover page content */}
          <button className={'absolute top-10 right-10 text-black'} onClick={() => close()}>
            <Icon icon="lucide:x" width="24" />
          </button>

          <div className={'flex flex-1 justify-center items-center bg-whites pl-[440px] py-5'}>
            <div
              className="bg-white rounded-xl p-8 border w-[70%] max-w-[calc(100vh*0.7)] h-full"
              style={{
                padding: '0.5in',
                borderColor: 'none',
              }}
              id={'cover-page-component'}
            >
              <div className="cover-page flex flex-col h-full justify-between">
                {(() => {
                  switch (coverPageDesign) {
                    case 1:
                    default:
                      return (
                        <CoverPageModel1
                          title={title}
                          variant={variant}
                          documentDate={documentDate}
                          proposedTo={proposedTo}
                          createdBy={createdBy}
                          logo={logo}
                          logoSize={logoSize}
                          documentProjectID={documentProjectID}
                        />
                      )
                    case 2:
                      return (
                        <CoverPageModel2
                          title={title}
                          variant={variant}
                          documentDate={documentDate}
                          proposedTo={proposedTo}
                          createdBy={createdBy}
                          logo={logo}
                          logoSize={logoSize}
                          documentProjectID={documentProjectID}
                        />
                      )
                    case 3:
                      return (
                        <CoverPageModel3
                          title={title}
                          variant={variant}
                          documentDate={documentDate}
                          proposedTo={proposedTo}
                          createdBy={createdBy}
                          logo={logo}
                          logoSize={logoSize}
                          documentProjectID={documentProjectID}
                        />
                      )
                    case 4:
                      return (
                        <CoverPageModel4
                          title={title}
                          variant={variant}
                          documentDate={documentDate}
                          proposedTo={proposedTo}
                          createdBy={createdBy}
                          logo={logo}
                          logoSize={logoSize}
                          documentProjectID={documentProjectID}
                        />
                      )
                  }
                })()}
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  )
}
